import {
  HierarchicalLayout,
  HierarchicalLayoutEdgeDescriptor,
  type IMapper,
  type ILayoutAlgorithm,
  type LayoutEdge,
  type LayoutEdgeLabel,
  type LayoutGraph,
  LayoutKeys,
  type LayoutNode,
  LayoutStageBase,
  OrientedRectangle,
  Point,
} from '@ardoq/yfiles';
import { stripColinearPoints } from 'tabview/blocks/misc/geometry';

/** Combined edges into one target, cut at one group around it. */
type Bundle = {
  edges: LayoutEdge[];
  target: LayoutNode;
  group: LayoutNode;
};

/** A bundle laid out as its edges ending at the group, and one edge on from the group's border. */
type Split = Bundle & {
  inner: LayoutEdge;
  innerDescriptor: HierarchicalLayoutEdgeDescriptor;
};

const isDescendantOf = (
  graph: LayoutGraph,
  node: LayoutNode,
  ancestor: LayoutNode
): boolean => {
  for (
    let parent = graph.getParent(node);
    parent;
    parent = graph.getParent(parent)
  ) {
    if (parent === ancestor) {
      return true;
    }
  }
  return false;
};

/** The outermost group around the edge's target that does not also hold its source. */
const outermostTargetGroup = (
  graph: LayoutGraph,
  { source, target }: LayoutEdge
): LayoutNode | null => {
  let outermost: LayoutNode | null = null;
  for (
    let group = graph.getParent(target);
    group && group !== source && !isDescendantOf(graph, source, group);
    group = graph.getParent(group)
  ) {
    outermost = group;
  }
  return outermost;
};

const hasSize = ({ layout: { width, height } }: LayoutEdgeLabel): boolean =>
  width > 0 && height > 0;

const pathOf = (edge: LayoutEdge): Point[] => [
  edge.sourcePortLocation,
  ...edge.bends.toArray().map(({ location }) => location),
  edge.targetPortLocation,
];

const setPath = (graph: LayoutGraph, edge: LayoutEdge, path: Point[]) => {
  edge.resetPath(false);
  edge.sourcePortLocation = path[0];
  edge.targetPortLocation = path[path.length - 1];
  path.slice(1, -1).forEach(({ x, y }) => graph.addBend(edge, x, y));
};

/** Joins an edge cut at a group back up with the inner edge, keeping its labels where the layout put them. */
const joinAt = (
  graph: LayoutGraph,
  edge: LayoutEdge,
  inner: LayoutEdge,
  target: LayoutNode
) => {
  const path = stripColinearPoints(
    [...pathOf(edge), ...pathOf(inner)],
    ({ x }) => x,
    ({ y }) => y
  );
  const labelLayouts = edge.labels.toArray().map(label => ({
    label,
    layout: new OrientedRectangle(label.layout),
  }));
  graph.changeEdge(edge, edge.source, target);
  setPath(graph, edge, path);
  labelLayouts.forEach(({ label, layout }) => {
    label.layout = layout;
  });
};

const hasLabelInGroup = ({ edges, group }: Bundle): boolean =>
  edges.some(({ labels }) =>
    labels.some(
      label =>
        hasSize(label) && group.layout.bounds.intersects(label.layout.bounds)
    )
  );

/** Remembers where every node, edge and label is, and returns a function that puts them back. */
const captureLayout = (graph: LayoutGraph): VoidFunction => {
  const nodes = graph.nodes.toArray();
  const edges = graph.edges.toArray();
  const nodeBounds = nodes.map(node => ({ node, bounds: node.layout.bounds }));
  const edgePaths = edges.map(edge => ({ edge, path: pathOf(edge) }));
  const labelLayouts = [
    ...nodes.flatMap(({ labels }) => labels.toArray()),
    ...edges.flatMap(({ labels }) => labels.toArray()),
  ].map(label => ({ label, layout: new OrientedRectangle(label.layout) }));
  return () => {
    nodeBounds.forEach(({ node, bounds }) => {
      node.layout.bounds = bounds;
    });
    edgePaths.forEach(({ edge, path }) => setPath(graph, edge, path));
    labelLayouts.forEach(({ label, layout }) => {
      label.layout = layout;
    });
  };
};

type EdgeMapper<T> = IMapper<LayoutEdge, T> | null;

const groupByBus = (
  edges: LayoutEdge[],
  busIds: IMapper<LayoutEdge, unknown>
): LayoutEdge[][] => [
  ...edges
    .reduce((bundles, edge) => {
      const busId = busIds.get(edge);
      if (busId) {
        bundles.set(busId, [...(bundles.get(busId) ?? []), edge]);
      }
      return bundles;
    }, new Map<unknown, LayoutEdge[]>())
    .values(),
];

/**
 * The bundle to split, or null when it has no label to make room for, when its
 * edges do not share a target, or when one of them has no group to cut at.
 * Every edge is cut at the innermost of their outermost target groups, so the
 * bundle keeps one trunk and its label stays on the part every edge shares.
 */
const toBundle = (graph: LayoutGraph, edges: LayoutEdge[]): Bundle | null => {
  const [{ target }] = edges;
  if (
    !edges.some(({ labels }) => labels.some(hasSize)) ||
    edges.some(edge => edge.target !== target)
  ) {
    return null;
  }
  const groups = edges.map(edge => outermostTargetGroup(graph, edge));
  if (!groups.every((group): group is LayoutNode => group !== null)) {
    return null;
  }
  const group = groups.reduce((innermost, candidate) =>
    isDescendantOf(graph, candidate, innermost) ? candidate : innermost
  );
  return { edges, target, group };
};

/**
 * Lays out each labeled bundle of combined edges into a component nested in
 * groups whose label the core layout puts inside the target's groups again, as
 * edges that end on the border of a group around the target and one edge on
 * from that border to the target. The labels are then placed, and given room,
 * outside that group. The edges are joined up again afterwards. When no label
 * lands inside a target's groups, the core layout's result stands.
 */
class SplitCombinedEdgesAtTargetGroupStage extends LayoutStageBase {
  constructor(
    coreLayout: ILayoutAlgorithm,
    private defaultEdgeDescriptor: HierarchicalLayoutEdgeDescriptor
  ) {
    super(coreLayout);
  }

  override applyLayoutImpl(graph: LayoutGraph): void {
    const { context } = graph;
    const targetEdgeGroupIds: EdgeMapper<unknown> = context.getItemData(
      LayoutKeys.TARGET_EDGE_GROUP_ID_DATA_KEY
    );
    const edgeDescriptors: EdgeMapper<HierarchicalLayoutEdgeDescriptor> =
      context.getItemData(HierarchicalLayout.EDGE_DESCRIPTOR_DATA_KEY);
    const sourcePortGroupIds: EdgeMapper<unknown> = context.getItemData(
      LayoutKeys.SOURCE_PORT_GROUP_ID_DATA_KEY
    );
    const targetPortGroupIds: EdgeMapper<unknown> = context.getItemData(
      LayoutKeys.TARGET_PORT_GROUP_ID_DATA_KEY
    );

    const bundles = targetEdgeGroupIds
      ? groupByBus(graph.edges.toArray(), targetEdgeGroupIds)
          .map(edges => toBundle(graph, edges))
          .filter((bundle): bundle is Bundle => bundle !== null)
      : [];

    if (!bundles.length) {
      this.coreLayout?.applyLayout(graph);
      return;
    }

    const restoreLayout = captureLayout(graph);
    this.coreLayout?.applyLayout(graph);
    const bundlesWithLabelInGroup = bundles.filter(hasLabelInGroup);
    if (!bundlesWithLabelInGroup.length) {
      return;
    }
    restoreLayout();

    const splits: Split[] = bundlesWithLabelInGroup.map(bundle => {
      const innerDescriptor = (
        edgeDescriptors?.get(bundle.edges[0]) ?? this.defaultEdgeDescriptor
      ).createCopy();
      innerDescriptor.directGroupContentEdgeRouting = true;
      return {
        ...bundle,
        inner: graph.createEdge(bundle.group, bundle.target),
        innerDescriptor,
      };
    });
    splits.forEach(({ edges, group }) =>
      edges.forEach(edge => graph.changeEdge(edge, edge.source, group))
    );

    const innerEdgeOfOuterEdge = new Map(
      splits.flatMap(({ edges, inner }) =>
        edges.map(edge => [edge, inner] as const)
      )
    );
    const innerDescriptors = new Map(
      splits.map(({ inner, innerDescriptor }) => [inner, innerDescriptor])
    );

    context.pushLayer();
    context.addItemData(
      LayoutKeys.TARGET_PORT_GROUP_ID_DATA_KEY,
      edge =>
        innerEdgeOfOuterEdge.get(edge) ?? targetPortGroupIds?.get(edge) ?? null
    );
    context.addItemData(LayoutKeys.SOURCE_PORT_GROUP_ID_DATA_KEY, edge =>
      innerDescriptors.has(edge)
        ? edge
        : (sourcePortGroupIds?.get(edge) ?? null)
    );
    context.addItemData(
      HierarchicalLayout.EDGE_DESCRIPTOR_DATA_KEY,
      edge => innerDescriptors.get(edge) ?? edgeDescriptors?.get(edge) ?? null
    );

    try {
      this.coreLayout?.applyLayout(graph);
    } finally {
      context.popLayer();
      splits.forEach(({ edges, target, inner }) => {
        edges.forEach(edge => joinAt(graph, edge, inner, target));
        graph.remove(inner);
      });
    }
  }
}

export { SplitCombinedEdgesAtTargetGroupStage };
